import React, { useEffect, useState } from 'react';
import { TrendingUp, Loader2, Sparkles } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ReadingPaceTrend — Iter 82 (Feb 2026).
 *
 * Compact 14-day chart of the reader's drill sessions.
 *   • X axis: date
 *   • Bars   : effective (adjusted) WPM per day (amber)
 *   • Line   : comprehension % per day, secondary Y (cyan)
 *
 * Pure SVG — no chart library needed. Zero external deps.
 *
 * Props:
 *   days?: number = 14
 *   compact?: bool  → shorter height for dashboard cards
 */
const ReadingPaceTrend = ({ days = 14, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/reader/pace-trend?days=${days}`);
        if (r.ok && alive) setData(await r.json());
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5 flex items-center justify-center h-40" data-testid="pace-trend-loading">
        <Loader2 className="w-5 h-5 animate-spin text-white/40" />
      </div>
    );
  }

  const rows = data?.days || [];
  const totals = data?.totals || {};
  const hasData = totals.sessions > 0;

  const H = compact ? 90 : 130;
  const PAD_T = 20, PAD_B = 24, PAD_X = 30;
  const W = 560;
  const maxWpm = Math.max(500, ...(rows.map(r => r.avg_adjusted_wpm) || [0]));
  const barW = (W - PAD_X * 2) / Math.max(1, rows.length) * 0.7;
  const gap = (W - PAD_X * 2) / Math.max(1, rows.length);

  const y = (v, max) => PAD_T + (H - PAD_T - PAD_B) * (1 - (v / max));
  const compPath = rows.map((r, i) => {
    const cx = PAD_X + gap * (i + 0.5);
    const cy = y(r.avg_comprehension_pct, 100);
    return `${i === 0 ? 'M' : 'L'} ${cx.toFixed(1)} ${cy.toFixed(1)}`;
  }).join(' ');

  return (
    <div
      className="rounded-2xl bg-[#12131A] border border-white/10 p-5 space-y-3"
      data-testid="pace-trend-card"
    >
      <header className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-400/30 grid place-items-center">
          <TrendingUp className="w-4 h-4 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-amber-300">Reading Pace Trend</p>
          <p className="font-display text-lg font-black text-white tracking-tight">
            {days}-day rollup
          </p>
        </div>
        {hasData && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-black">Best</p>
            <p className="text-lg font-black text-amber-300 tabular-nums">
              {totals.best_adjusted_wpm} wpm
            </p>
          </div>
        )}
      </header>

      {!hasData ? (
        <div className="text-center py-8" data-testid="pace-trend-empty">
          <Sparkles className="w-6 h-6 text-white/20 mx-auto mb-2" />
          <p className="text-sm text-white/50">
            Finish a drill to unlock your pace trend.
          </p>
        </div>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="pace-trend-svg">
            {/* baseline */}
            <line x1={PAD_X} x2={W - PAD_X} y1={H - PAD_B} y2={H - PAD_B}
                  stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            {/* WPM bars */}
            {rows.map((r, i) => {
              const bx = PAD_X + gap * (i + 0.5) - barW / 2;
              const by = y(r.avg_adjusted_wpm, maxWpm);
              const bh = (H - PAD_B) - by;
              return (
                <rect
                  key={r.date}
                  x={bx} y={by} width={barW} height={Math.max(0, bh)}
                  fill={r.sessions > 0 ? 'rgba(245,194,66,0.65)' : 'rgba(255,255,255,0.05)'}
                  rx="2"
                >
                  <title>
                    {r.date} — {r.sessions} sessions · {r.avg_adjusted_wpm} wpm · {r.avg_comprehension_pct}% comp
                  </title>
                </rect>
              );
            })}
            {/* comprehension line */}
            {compPath && (
              <path d={compPath} fill="none" stroke="rgb(103, 232, 249)" strokeWidth="1.5" />
            )}
            {/* comprehension dots */}
            {rows.map((r, i) => {
              if (!r.sessions) return null;
              const cx = PAD_X + gap * (i + 0.5);
              const cy = y(r.avg_comprehension_pct, 100);
              return <circle key={`d-${r.date}`} cx={cx} cy={cy} r="2.5" fill="rgb(103, 232, 249)" />;
            })}
          </svg>
          <footer className="grid grid-cols-3 gap-2 pt-1">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-black">Avg WPM</p>
              <p className="text-base font-black text-amber-200 tabular-nums" data-testid="pace-trend-avg-wpm">
                {totals.avg_adjusted_wpm}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-black">Avg comp</p>
              <p className="text-base font-black text-cyan-300 tabular-nums" data-testid="pace-trend-avg-comp">
                {totals.avg_comprehension_pct}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-black">Sessions</p>
              <p className="text-base font-black text-white tabular-nums" data-testid="pace-trend-sessions">
                {totals.sessions}
              </p>
            </div>
          </footer>
        </>
      )}
    </div>
  );
};

export default ReadingPaceTrend;
